import React from "react";
import {browserHistory} from "react-router";
import {executeQuery} from "../../requests/executeQuery";
import {getData} from "../../requests/CRUD";
import {formatPropertyName, writeMailMergeSelection} from "../../utils";
import {can} from "../../access";
import {AUDIENCE_DOWNLOADERS} from "../MailMerge/merge";
import {stringISOtoUS} from "../../date-converter";
import Icon from "../Icon";
import svgSort from '../../svgs/sort.svg';
import svgSortAsc from '../../svgs/sort-asc.svg';
import svgSortDesc from '../../svgs/sort-desc.svg';
import {PROPERTIES} from "../../consts/tables";
import {PROPERTY_WEBSITE_USERS_V2} from "../../consts/queries";
import Input from "../Input-new/Input";

const cloneDeep = require('lodash.clonedeep');
const findIndex = require('lodash.findindex');

export default class PropertyStatistics extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            columns: [{
                label: 'First Name',
                key: 'FIRST_NAME',
            }, {
                label: 'Last Name',
                key: 'LAST_NAME',
            }, {
                label: 'Email',
                key: 'EMAIL',
            }, {
                label: 'Phone',
                key: 'PHONE',
            }, {
                label: 'Company',
                key: 'COMPANY',
            }, {
                label: 'Type',
                key: 'TYPE',
                sort: 'default',
            }, {
                label: 'Downloads',
                key: 'DOWNLOAD_COUNT',
                sort: 'default',
            },{
                label: 'First Download',
                key: 'FIRST_DOWNLOAD',
                sort: 'default',
            },{
                label: 'Last Download',
                key: 'LAST_DOWNLOAD',
                sort: 'default',
            }],
            data: [],
            original: [],
            loading: true,
            propertyInfo: null,
        };

        this.sortTable = this.sortTable.bind(this);
        this.sortData = this.sortData.bind(this);
        this.startMailMerge = this.startMailMerge.bind(this);

    }

    componentDidMount() {
        this.fetch(this.props.params.id);
    }

    fetch(id) {
        this.setState({loading: true});
        const propertyOptions = {
            limit: {
                count: 1,
                offset: 0,
            },
            column: [
                'NAME',
                'CITY',
                'STATE',
            ],
            filter: [{
                code: 'ARCIMEDES_ROW_ID',
                text: id,
                type: 'exact',
            }],
        };

        Promise.all([
            getData({tableCode: PROPERTIES, options: propertyOptions}),
            executeQuery({queryName: PROPERTY_WEBSITE_USERS_V2, options: {param: {PROPERTY_ID: id}}}),
        ]).then(res => {
            if (res && res[0] && res[0].success && Array.isArray(res[0].data) &&
                res[1] && res[1].success && Array.isArray(res[1].data)) {
                const property = res[0].data[0];
                const propertyName = property && property.NAME ? property.NAME : '';
                const propertyCity = property && property.CITY ? property.CITY : '';
                const propertyState = property && property.STATE ? property.STATE : '';
                const propertyInfo = formatPropertyName(propertyName, propertyCity, propertyState);
                const columns = this.state.columns;
                let data = PropertyStatistics.buildTable(columns, res[1].data);
                this.setState({
                    data,
                    original: cloneDeep(data),
                    propertyInfo,
                });
            }
            this.setState({loading: false});
        });
    }
    /**
     * The value a column sorts on, which is not always the one it displays:
     * a date cell holds mm/dd/yyyy, which orders by month as text, and a
     * count read off the row can arrive as a string.
     */
    static sortValue(row, key) {
        if (key === 'FIRST_DOWNLOAD' || key === 'LAST_DOWNLOAD') {
            return row[`${key}_RAW`] || '';
        }
        if (key === 'DOWNLOAD_COUNT') {
            return parseInt(row[key], 10) || 0;
        }
        return (row[key] === null || row[key] === undefined) ? '' : row[key];
    }

    sortData(array, key, desc = false) {
        const copyArray = cloneDeep(array);

        if (!key) {
            return copyArray;
        }

        // Blank cells collect at the bottom in both directions: a row with no
        // download date is missing the value being sorted, not the earliest one.
        const isEmpty = value => value === '' || value === null || value === undefined;
        const withValue = [];
        const withoutValue = [];

        copyArray.forEach(item => {
            if (isEmpty(PropertyStatistics.sortValue(item, key))) {
                withoutValue.push(item);
            } else {
                withValue.push(item);
            }
        });

        withValue.sort((a, b) => {
            const valueA = PropertyStatistics.sortValue(a, key);
            const valueB = PropertyStatistics.sortValue(b, key);
            let comparison;

            if (typeof valueA === 'number' && typeof valueB === 'number') {
                comparison = valueA - valueB;
            } else {
                comparison = String(valueA).localeCompare(String(valueB), undefined, {sensitivity: 'base'});
            }

            return desc ? -comparison : comparison;
        });

        return [...withValue, ...withoutValue];
    }

    sortTable(key) {
        let {data, columns} = {...this.state};
        const index = findIndex(columns, {key});

        if (columns[index] && columns[index].sort) {
            // old direction = 'default' (desc by ARC_ROW_ID) => new direction = 'asc'
            // old direction = 'asc' => new direction = 'desc'
            // old direction = 'desc' => new direction = 'default'
            columns[index].sort = columns[index].sort === 'asc' ? 'desc' : (columns[index].sort === 'desc' ? 'default' : 'asc');
            if (columns[index].sort === 'default') {
                data = this.state.original;
            } else {
                const isDesc = columns[index].sort !== 'asc';
                //data = sortArray(this.state.original, key, isDesc);
                data = this.sortData(this.state.original, key, isDesc);
                columns.map((column, colIndex) => {
                    if(column.sort && column.sort !== 'default' && index !== colIndex) {
                        column.sort = 'default';
                    }
                });
            }
        }

        this.setState({data, columns});
    }

    static normalizeRows(rows) {
        const seen = {};
        const result = [];
        for (const row of rows) {
            const userType = (row.USER_TYPE || '').toLowerCase().trim();
            // Trust the table the record actually matched over the tracked USER_TYPE,
            // which can be wrong (e.g. saved as 'broker' while the record lives in
            // INVESTORS). Fall back to USER_TYPE only when both tables matched the
            // same id, or neither did.
            const hasAgent = !!(row.AGENT_EMAIL || row.AGENT_FIRST_NAME || row.AGENT_LAST_NAME || row.AGENT_PHONE || row.AGENT_LEAD_ID);
            const hasInvestor = !!(row.INVESTOR_EMAIL || row.INVESTOR_NAME || row.INVESTOR_PHONE || row.INVESTOR_LEAD_ID);
            let isAgent, isInvestor;
            if (hasAgent && !hasInvestor) {
                isAgent = true;
                isInvestor = false;
            } else if (hasInvestor && !hasAgent) {
                isAgent = false;
                isInvestor = true;
            } else {
                isAgent = userType === 'broker';
                isInvestor = userType === 'investor';
            }
            let firstName = isAgent ? (row.AGENT_FIRST_NAME || '') : '';
            let lastName = isAgent ? (row.AGENT_LAST_NAME || '') : '';
            if (!firstName && row.INVESTOR_NAME) {
                const parts = row.INVESTOR_NAME.split(' ');
                firstName = parts[0] || '';
                lastName = parts.slice(1).join(' ') || '';
            }
            const email = (isAgent ? (row.AGENT_EMAIL || '') : (row.INVESTOR_EMAIL || '')).toLowerCase().trim();
            const normalized = {
                EMAIL: isAgent ? (row.AGENT_EMAIL || '') : (row.INVESTOR_EMAIL || ''),
                FIRST_NAME: firstName,
                LAST_NAME: lastName,
                PHONE: isAgent ? (row.AGENT_PHONE || '') : (row.INVESTOR_PHONE || ''),
                COMPANY: '',
                IS_INVESTOR: isInvestor ? 1 : 0,
                IS_BROKER: isAgent ? 1 : 0,
                LEAD_ID: isAgent ? (row.AGENT_LEAD_ID || '') : isInvestor ? (row.INVESTOR_LEAD_ID || '') : '',
                DOWNLOAD_COUNT: row.DOWNLOAD_COUNT || 0,
                FIRST_DOWNLOAD: row.FIRST_DOWNLOAD || '',
                LAST_DOWNLOAD: row.LAST_DOWNLOAD || '',
            };
            if (email && seen[email]) {
                seen[email].DOWNLOAD_COUNT = parseInt(seen[email].DOWNLOAD_COUNT || 0) + parseInt(normalized.DOWNLOAD_COUNT || 0);
                if (normalized.FIRST_DOWNLOAD && (!seen[email].FIRST_DOWNLOAD || normalized.FIRST_DOWNLOAD < seen[email].FIRST_DOWNLOAD)) {
                    seen[email].FIRST_DOWNLOAD = normalized.FIRST_DOWNLOAD;
                }
                if (normalized.LAST_DOWNLOAD && (!seen[email].LAST_DOWNLOAD || normalized.LAST_DOWNLOAD > seen[email].LAST_DOWNLOAD)) {
                    seen[email].LAST_DOWNLOAD = normalized.LAST_DOWNLOAD;
                }
                continue;
            }
            if (email) seen[email] = normalized;
            result.push(normalized);
        }
        return result;
    }

    static buildTable(columns, res) {
        const rows = PropertyStatistics.normalizeRows(res);
        const data = [];
        rows.map((row, index) => {
            data[index] = { LEAD_ID: row.LEAD_ID || '', IS_BROKER: row.IS_BROKER, IS_FROM_CRM: row.IS_FROM_CRM };
            columns.map(column => {
                if (column.key === 'TYPE') {
                    data[index][column.key] = parseInt(row.IS_INVESTOR) === 1 ? 'Investor' : parseInt(row.IS_BROKER) === 1 ? 'Broker' : '';
                    return;
                }
                if (column.key === 'PHONE') {
                    data[index][column.key] = row[column.key] || '';
                    return;
                }
                if (column.key === 'FIRST_DOWNLOAD' || column.key === 'LAST_DOWNLOAD') {
                    data[index][column.key] = row[column.key] ? stringISOtoUS(row[column.key].split(' ')[0]) : '';
                    // The cell is mm/dd/yyyy for reading; sorting needs the
                    // original ISO timestamp, which still orders as a string.
                    data[index][`${column.key}_RAW`] = row[column.key] || '';
                    return;
                }
                if (column.key === 'DOWNLOAD_COUNT') {
                    data[index][column.key] = parseInt(row[column.key], 10) || 0;
                    return;
                }
                data[index][column.key] = row[column.key] || '';
            });
        });
        return data;
    }

    renderTable(columns, data, loading) {
        const message = loading ? 'Loading data...' : 'No data available';
        return (
            <React.Fragment>
                {
                    data.length ?
                        <table className='table'>
                            <thead>
                            <tr>
                                {
                                    columns.map((col, index) => {
                                        return (
                                            <th className={`th head border_top ${index === 0 ? 'border_left' : ''}`}
                                                key={col.key}>
                                                {
                                                    col.sort ?
                                                        <React.Fragment>
                                                            {col.label}
                                                            <div className={`sort-icon ${col.sort !== 'default' ? 'active' : ''}`}
                                                                 onClick={() => this.sortTable(col.key)}>
                                                                <Icon svg={col.sort === 'asc' ? svgSortAsc : col.sort === 'desc' ? svgSortDesc : svgSort}/>
                                                            </div>
                                                        </React.Fragment>
                                                        :
                                                        col.label
                                                }
                                            </th>
                                        )
                                    })
                                }
                            </tr>
                            </thead>
                            <tbody>
                            {
                                data.map((row, index) => {
                                    return (
                                        <tr key={index}>
                                            {
                                                columns.map((col, colIndex) => {
                                                    return (
                                                        <td className={`td ${colIndex === 0 ? 'border_left' : ''}`}
                                                            key={`${index}-${colIndex}`}
                                                            style={{textAlign: col.style ? col.style : ''}}>
                                                            {
                                                                (col.key === 'FIRST_NAME' || col.key === 'LAST_NAME') && row.LEAD_ID ?
                                                                    <a href={`https://accounts.kore.co/crm/${row.IS_BROKER ? 'vendor' : 'lead'}/${row.LEAD_ID}`} target="_blank" rel="noopener noreferrer">{row[col.key]}</a>
                                                                    :
                                                                col.key === 'PHONE' && row[col.key] ?
                                                                    <Input valueType="phone" value={row[col.key]} viewMode={true} className="no-margin" />
                                                                    :
                                                                col.key === 'EMAIL' ?
                                                                    <a href={`mailto:${row[col.key] ? row[col.key] : ''}`}>{row[col.key]}</a>
                                                                    :
                                                                    row[col.key]
                                                            }
                                                        </td>
                                                    )
                                                })
                                            }
                                        </tr>
                                    )
                                })
                            }
                            </tbody>
                        </table>
                        :
                        <div className='no-data-found'>{message}</div>
                }
            </React.Fragment>
        )
    }

    /**
     * Opens the mail merge for everyone who downloaded this property's
     * marketing package. The server works the recipients out again from the
     * download rows, so only the property travels.
     */
    startMailMerge() {
        writeMailMergeSelection({
            audience: AUDIENCE_DOWNLOADERS,
            propertyIds: [String(this.props.params.id)],
            propertyName: this.state.propertyInfo || '',
            capturedAt: new Date().toISOString(),
        });
        browserHistory.push('/mail-merge');
    }

    render() {
        const {columns, data, loading, propertyInfo} = this.state;

        return (
            <div className='property-statistics-container'>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap'}}>
                        <h4 className='property-info' style={{margin: 0}}>{propertyInfo}</h4>
                        {can('mail_merge') ?
                            <button
                                type='button'
                                className='btn btn-default property-statistics-mail-merge'
                                onClick={this.startMailMerge}
                                disabled={loading || data.length === 0}
                                title={!loading && data.length === 0 ? 'Nobody has downloaded this package yet' : 'Email everyone who downloaded this package'}
                            >
                                Mail Merge
                            </button>
                            : null}
                    </div>
                    <button onClick={() => window.history.back()} className='btn btn-default'>Back</button>
                </div>
                {this.renderTable(columns, data, loading)}
            </div>
        )
    }
}